import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import type { ResourcePlan } from '@claude-code-api/shared';

export default function AdminResourcePlansPage() {
  const { t } = useTranslation(['adminResourcePlans', 'common']);
  const [plans, setPlans] = useState<ResourcePlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<ResourcePlan | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formName, setFormName] = useState('');
  const [formCpu, setFormCpu] = useState(2);
  const [formMem, setFormMem] = useState(4);
  const [formStorage, setFormStorage] = useState(20);
  const [formCredits, setFormCredits] = useState(15000);
  const [formPrice, setFormPrice] = useState(15);
  const [formOrder, setFormOrder] = useState(0);

  const fetchPlans = async () => {
    try {
      const res = await fetch('/api/admin/resource-plans', {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setPlans(data.data || []);
      }
    } catch {
      toast.error(t('adminResourcePlans:errors.load'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const openCreate = () => {
    setEditingPlan(null);
    setFormName('');
    setFormCpu(2);
    setFormMem(4);
    setFormStorage(20);
    setFormCredits(15000);
    setFormPrice(15);
    setFormOrder(0);
    setShowModal(true);
  };

  const openEdit = (plan: ResourcePlan) => {
    setEditingPlan(plan);
    setFormName(plan.name);
    setFormCpu(Number(plan.cpuCores));
    setFormMem(Number(plan.memoryGb));
    setFormStorage(Number(plan.storageGb));
    setFormCredits(Number(plan.monthlyCredits));
    setFormPrice(Number(plan.monthlyPriceUsd));
    setFormOrder(plan.displayOrder);
    setShowModal(true);
  };

  const savePlan = async () => {
    setSaving(true);
    try {
      const body = {
        name: formName,
        cpuCores: formCpu,
        memoryGb: formMem,
        storageGb: formStorage,
        monthlyCredits: formCredits,
        monthlyPriceUsd: formPrice,
        displayOrder: formOrder,
      };

      const url = editingPlan
        ? `/api/admin/resource-plans/${editingPlan.id}`
        : '/api/admin/resource-plans';
      const method = editingPlan ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (res.ok) {
        toast.success(
          editingPlan
            ? t('adminResourcePlans:messages.updated')
            : t('adminResourcePlans:messages.created'),
        );
        setShowModal(false);
        fetchPlans();
      } else {
        const data = await res.json();
        toast.error(data.error || t('adminResourcePlans:errors.save'));
      }
    } catch {
      toast.error(t('adminResourcePlans:errors.save'));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (plan: ResourcePlan) => {
    try {
      const res = await fetch(`/api/admin/resource-plans/${plan.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ isActive: !plan.isActive }),
      });
      if (res.ok) {
        toast.success(
          plan.isActive
            ? t('adminResourcePlans:messages.deactivated')
            : t('adminResourcePlans:messages.activated'),
        );
        fetchPlans();
      }
    } catch {
      toast.error(t('adminResourcePlans:errors.update'));
    }
  };

  const setDefault = async (plan: ResourcePlan) => {
    try {
      const res = await fetch(`/api/admin/resource-plans/${plan.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ isDefault: true }),
      });
      if (res.ok) {
        toast.success(
          t('adminResourcePlans:messages.setDefault', { name: plan.name }),
        );
        fetchPlans();
      }
    } catch {
      toast.error(t('adminResourcePlans:errors.setDefault'));
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title={t('adminResourcePlans:title')}
          description={t('adminResourcePlans:descriptionShort')}
        />
        <Skeleton className="h-48 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <PageHeader
          title={t('adminResourcePlans:title')}
          description={t('adminResourcePlans:description')}
        />
        <Button onClick={openCreate}>
          {t('adminResourcePlans:actions.create')}
        </Button>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('adminResourcePlans:table.name')}</TableHead>
              <TableHead>{t('adminResourcePlans:table.cpu')}</TableHead>
              <TableHead>{t('adminResourcePlans:table.ram')}</TableHead>
              <TableHead>{t('adminResourcePlans:table.storage')}</TableHead>
              <TableHead>
                {t('adminResourcePlans:table.creditsPerMonth')}
              </TableHead>
              <TableHead>{t('adminResourcePlans:table.usdPerMonth')}</TableHead>
              <TableHead>{t('adminResourcePlans:table.status')}</TableHead>
              <TableHead>{t('adminResourcePlans:table.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {plans.map((plan) => (
              <TableRow key={plan.id}>
                <TableCell className="font-medium">
                  {plan.name}
                  {plan.isDefault && (
                    <Badge variant="outline" className="ml-2">
                      {t('adminResourcePlans:status.default')}
                    </Badge>
                  )}
                </TableCell>
                <TableCell>{Number(plan.cpuCores)}</TableCell>
                <TableCell>
                  {t('adminResourcePlans:table.gbValue', {
                    value: Number(plan.memoryGb),
                  })}
                </TableCell>
                <TableCell>
                  {t('adminResourcePlans:table.gbValue', {
                    value: Number(plan.storageGb),
                  })}
                </TableCell>
                <TableCell>
                  {Number(plan.monthlyCredits).toLocaleString()}
                </TableCell>
                <TableCell>
                  ${Number(plan.monthlyPriceUsd).toFixed(0)}
                </TableCell>
                <TableCell>
                  <Badge variant={plan.isActive ? 'default' : 'secondary'}>
                    {plan.isActive
                      ? t('adminResourcePlans:status.active')
                      : t('adminResourcePlans:status.inactive')}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(plan)}
                    >
                      {t('adminResourcePlans:actions.edit')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleActive(plan)}
                    >
                      {plan.isActive
                        ? t('adminResourcePlans:actions.deactivate')
                        : t('adminResourcePlans:actions.activate')}
                    </Button>
                    {!plan.isDefault && plan.isActive && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDefault(plan)}
                      >
                        {t('adminResourcePlans:actions.setDefault')}
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingPlan
                ? t('adminResourcePlans:dialog.editTitle')
                : t('adminResourcePlans:dialog.createTitle')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('adminResourcePlans:form.name')}</Label>
              <Input
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder={t('adminResourcePlans:form.namePlaceholder')}
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>{t('adminResourcePlans:form.cpuCores')}</Label>
                <Input
                  type="number"
                  min={0.5}
                  step={0.5}
                  value={formCpu}
                  onChange={(e) => setFormCpu(Number(e.target.value))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('adminResourcePlans:form.ram')}</Label>
                <Input
                  type="number"
                  min={1}
                  step={1}
                  value={formMem}
                  onChange={(e) => setFormMem(Number(e.target.value))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('adminResourcePlans:form.storage')}</Label>
                <Input
                  type="number"
                  min={1}
                  step={1}
                  value={formStorage}
                  onChange={(e) => setFormStorage(Number(e.target.value))}
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>{t('adminResourcePlans:form.creditsPerMonth')}</Label>
                <Input
                  type="number"
                  min={0}
                  value={formCredits}
                  onChange={(e) => setFormCredits(Number(e.target.value))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('adminResourcePlans:form.usdPerMonth')}</Label>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={formPrice}
                  onChange={(e) => setFormPrice(Number(e.target.value))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('adminResourcePlans:form.displayOrder')}</Label>
                <Input
                  type="number"
                  min={0}
                  value={formOrder}
                  onChange={(e) => setFormOrder(Number(e.target.value))}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              {t('common:cancel')}
            </Button>
            <Button onClick={savePlan} disabled={saving || !formName}>
              {saving
                ? t('adminResourcePlans:actions.saving')
                : editingPlan
                  ? t('adminResourcePlans:actions.update')
                  : t('adminResourcePlans:actions.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
